<template>
    <div class="ops-board">
        <div class="page-title d-flex justify-content-between align-items-center flex-wrap">
            <div class="title">
                <h3>Operations</h3>
                <p class="text-muted mb-0">Cross-trip fulfilment board</p>
            </div>
            <div>
                <el-button size="small" @click="load">Refresh</el-button>
                <el-button size="small" type="primary" tag="a" href="/operations/templates" v-if="canTemplates">Templates</el-button>
            </div>
        </div>

        <el-card class="mb-3" shadow="never">
            <el-form :inline="true" label-position="top" @submit.prevent>
                <el-form-item label="Search">
                    <el-input v-model="filters.search" clearable placeholder="CDM / name / dest" size="small" />
                </el-form-item>
                <el-form-item label="Health">
                    <el-select v-model="filters.health" clearable size="small" style="width: 140px">
                        <el-option label="On track" value="on_track" />
                        <el-option label="Warning" value="warning" />
                        <el-option label="At risk" value="at_risk" />
                        <el-option label="Critical" value="critical" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Stage">
                    <el-select v-model="filters.stage" clearable size="small" style="width: 140px">
                        <el-option label="Booking" value="booking" />
                        <el-option label="Fulfilment" value="fulfilment" />
                        <el-option label="QA" value="qa" />
                        <el-option label="Pre-trip" value="pre_trip" />
                        <el-option label="In-trip" value="in_trip" />
                        <el-option label="Closure" value="closure" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Overdue only">
                    <el-switch v-model="filters.overdue_only" />
                </el-form-item>
                <el-form-item label=" ">
                    <el-button type="primary" size="small" @click="page = 1; load()">Filter</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <div v-loading="loading">
            <el-card
                v-for="row in rows"
                :key="row.id"
                class="mb-2 trip-card"
                shadow="hover"
                @click="openTrip(row.enquiry_id)"
            >
                <div class="d-flex justify-content-between align-items-start flex-wrap">
                    <div>
                        <strong>{{ row.trip_label }}</strong>
                        <span class="ml-2">{{ row.customer }}</span>
                        <div class="text-muted small">
                            {{ row.destinations }}
                            <span v-if="row.departure_date"> · Dep {{ row.departure_date }}</span>
                            · {{ row.stage_label }}
                        </div>
                    </div>
                    <div class="text-right">
                        <el-tag :type="healthType(row.health)" size="small">{{ healthLabel(row.health) }}</el-tag>
                        <div class="mt-1 small">
                            {{ row.tasks_complete }}/{{ row.tasks_total }} · {{ row.completion_percent }}%
                            <span v-if="row.overdue" class="text-danger"> · {{ row.overdue }} overdue</span>
                            <span v-if="row.blocked"> · {{ row.blocked }} blocked</span>
                        </div>
                        <el-progress :percentage="Number(row.completion_percent)" :stroke-width="8" style="width: 160px; margin-top: 6px" />
                    </div>
                </div>
            </el-card>

            <el-empty v-if="!loading && rows.length === 0" description="No trips with checklists yet" />

            <div class="d-flex justify-content-center mt-3" v-if="meta.last_page > 1">
                <el-pagination
                    layout="prev, pager, next"
                    :page-count="meta.last_page"
                    :current-page="page"
                    @current-change="onPage"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import axios from 'axios';

const loading = ref(false);
const rows = ref([]);
const page = ref(1);
const meta = reactive({ last_page: 1, total: 0 });
const filters = reactive({
    search: '',
    health: '',
    stage: '',
    overdue_only: false,
});
const canTemplates = ref(true);

const load = async () => {
    loading.value = true;
    try {
        const { data } = await axios.post('/operations/list', {
            ...filters,
            page: page.value,
            per_page: 20,
        });
        rows.value = data.data || [];
        Object.assign(meta, data.meta || {});
    } finally {
        loading.value = false;
    }
};

const onPage = (p) => {
    page.value = p;
    load();
};

const openTrip = (id) => {
    window.location.href = '/operations/trips/' + id;
};

const healthLabel = (h) => ({
    on_track: 'On track',
    warning: 'Warning',
    at_risk: 'At risk',
    critical: 'Critical',
}[h] || h);

const healthType = (h) => ({
    on_track: 'success',
    warning: 'warning',
    at_risk: 'warning',
    critical: 'danger',
}[h] || 'info');

onMounted(load);
</script>

<style scoped>
.ops-board { padding: 12px 4px 40px; }
.trip-card { cursor: pointer; }
.trip-card:hover { border-color: #409eff; }
</style>
